import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Play, Video } from "lucide-react";
import { Reveal } from "./Reveal";
import { TESTIMONIALS as DEFAULT_TESTIMONIALS, IMAGES } from "../../data/content";
import { api } from "../../lib/api";

const openVideo = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const withDefaults = (t, i) => ({
  name: t.name,
  role: t.role,
  quote: t.quote,
  videoUrl: t.videoUrl || null,
  cover: t.coverImageUrl || IMAGES.testimonials[i % IMAGES.testimonials.length],
});

export const Testimonials = () => {
  const [items, setItems] = useState(() =>
    DEFAULT_TESTIMONIALS.map((t, i) => withDefaults(t, i))
  );

  useEffect(() => {
    api
      .get("/testimonials")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) {
          setItems(data.map((t, i) => withDefaults(t, i)));
        }
      })
      .catch(() => {});
  }, []);

  const anyVideo = items.some((t) => t.videoUrl);
  return (
    <section
      data-testid="testimonials-section"
      className="relative bg-pblv-bg2 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8">
        <div className="mb-16 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <Reveal>
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.25em] text-pblv-mauve">
                Elles témoignent
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl font-medium leading-[1.05] tracking-tight text-pblv-ink md:text-6xl">
                Des T'POZ
                <br />
                <span className="italic">qui changent tout.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="inline-flex items-center gap-2 rounded-full border border-pblv-mauve/20 bg-pblv-mauve/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-pblv-mauvedark">
              <Video className="h-3.5 w-3.5" strokeWidth={2} />
              {anyVideo ? "Vidéos disponibles" : "Vidéos à venir"}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {items.map((t, i) => (
            <Reveal key={i} delay={i * 0.12} y={40}>
              <motion.figure
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 240, damping: 20 }}
                data-testid={`testimonial-card-${i}`}
                className={`flex h-full flex-col overflow-hidden rounded-[2rem] border border-pblv-ink/10 bg-white shadow-[0_8px_30px_rgba(45,40,48,0.04)] ${
                  i === 1 ? "md:mt-10" : ""
                }`}
              >
                {/* Video / cover area */}
                <button
                  type="button"
                  onClick={() => openVideo(t.videoUrl)}
                  data-testid={`testimonial-play-${i}`}
                  disabled={!t.videoUrl}
                  className="group relative h-52 w-full overflow-hidden bg-pblv-ink/5 focus:outline-none focus:ring-2 focus:ring-pblv-mauve"
                  aria-label={t.videoUrl ? `Regarder le témoignage de ${t.name}` : `Témoignage vidéo de ${t.name} à venir`}
                >
                  <img
                    src={t.cover}
                    alt={t.name}
                    className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-pblv-ink/60 via-pblv-ink/10 to-transparent" />
                  <div
                    className={`absolute inset-0 flex items-center justify-center transition-transform duration-500 group-hover:scale-110 ${t.videoUrl ? "" : "opacity-70"}`}
                  >
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-pblv-mauve shadow-[0_10px_30px_rgba(0,0,0,0.25)] backdrop-blur">
                      <Play className="ml-1 h-6 w-6 fill-current" strokeWidth={0} />
                    </span>
                  </div>
                  <p className="absolute bottom-3 left-4 rounded-full bg-white/85 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-pblv-ink backdrop-blur">
                    {t.videoUrl ? "Voir la vidéo" : "Vidéo à venir"}
                  </p>
                </button>

                <div className="flex flex-1 flex-col p-8">
                  <Quote className="h-8 w-8 text-pblv-rose" strokeWidth={1.5} />
                  <blockquote className="mt-4 flex-1 font-serif text-xl leading-snug text-pblv-ink md:text-2xl">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-6 border-t border-pblv-ink/10 pt-5">
                    <p className="text-sm font-semibold text-pblv-ink">
                      {t.name}
                    </p>
                    <p className="text-xs text-pblv-inksoft">{t.role}</p>
                  </figcaption>
                </div>
              </motion.figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
